import { reactive, readonly, toRefs } from 'vue';

import { useBankTransferAPIService } from '@/composables/services/useBankTransferAPIService';
import { type ClientEECode, useErrorModal } from '@/composables/useErrorModal';
import { addSentryBreadcrumb } from '@/logs/sentry';
import {
  type BankTransferRecipient,
  BankTransferRecipientType,
  type CheckIBANResponse
} from '@/references/axios/bankTransfer/types';
import { ExpectedError } from '@/references/ExpectedError';

const state = reactive<{
  recipients: Array<BankTransferRecipient>;
  isLoading: boolean;
  isLoaded: boolean;
}>({
  recipients: [],
  isLoading: false,
  isLoaded: false
});

async function checkIban(iban: string): Promise<CheckIBANResponse> {
  try {
    return await useBankTransferAPIService().checkIBANRequest(iban);
  } catch (error) {
    const toast = useErrorModal();
    addSentryBreadcrumb({
      level: 'debug',
      message: 'Failed to check iban',
      data: {
        error
      }
    });
    toast.auto(new ExpectedError<ClientEECode>('checkIban', { cause: error, sentryHandle: true }));
    throw error;
  }
}

async function loadRecipients(): Promise<void> {
  try {
    state.isLoading = true;
    state.recipients = await useBankTransferAPIService().getBankTransferRecipients();
  } catch (error) {
    const toast = useErrorModal();
    addSentryBreadcrumb({
      level: 'debug',
      message: 'Failed to get bank transfer recipients',
      data: {
        error
      }
    });
    toast.auto(
      new ExpectedError<ClientEECode>('loadRecipients', { cause: error, sentryHandle: true })
    );
  } finally {
    state.isLoading = false;

    if (!state.isLoaded) {
      state.isLoaded = true;
    }
  }
}

async function createRecipient(
  type: BankTransferRecipientType,
  firstName: string,
  lastName: string,
  nickname: string,
  iban: string,
  bic: string,
  bankName: string
): Promise<void> {
  try {
    state.recipients = await useBankTransferAPIService().createBankTransferRecipient(
      type,
      firstName,
      lastName,
      nickname,
      iban,
      bic,
      bankName
    );
  } catch (error) {
    const toast = useErrorModal();
    addSentryBreadcrumb({
      level: 'debug',
      message: 'Failed to create new bank transfer recipient',
      data: {
        error
      }
    });
    toast.auto(
      new ExpectedError<ClientEECode>('createRecipient', { cause: error, sentryHandle: true })
    );
    throw error;
  }
}

async function editRecipient(
  id: number,
  type: BankTransferRecipientType,
  firstName: string,
  lastName: string,
  nickname: string,
  iban: string,
  bic: string,
  bankName: string
): Promise<void> {
  try {
    const recipients = await useBankTransferAPIService().updateBankTransferRecipient(
      id,
      type,
      firstName,
      lastName,
      nickname,
      iban,
      bic,
      bankName
    );

    if (recipients?.length) {
      state.recipients = recipients;
    } else {
      state.recipients = [];
    }
  } catch (error) {
    const toast = useErrorModal();
    addSentryBreadcrumb({
      level: 'debug',
      message: 'Failed to edit bank transfer recipient',
      data: {
        error
      }
    });
    toast.auto(
      new ExpectedError<ClientEECode>('editRecipient', { cause: error, sentryHandle: true })
    );
    throw error;
  }
}

async function removeRecipient(id: number): Promise<void> {
  const savedRecipients = state.recipients;
  state.recipients = state.recipients.filter((item) => item.id !== id);

  try {
    const recipients = await useBankTransferAPIService().removeBankTransferRecipient(id);

    if (recipients?.length) {
      state.recipients = recipients;
    } else {
      state.recipients = [];
    }
  } catch (error) {
    state.recipients = savedRecipients;

    const toast = useErrorModal();
    addSentryBreadcrumb({
      level: 'debug',
      message: 'Failed to remove bank transfer recipient',
      data: {
        error
      }
    });
    toast.auto(
      new ExpectedError<ClientEECode>('removeRecipient', { cause: error, sentryHandle: true })
    );
    throw error;
  }
}

export const useBankTransfer = () => {
  return {
    ...toRefs(readonly(state)),
    checkIban,
    loadRecipients,
    createRecipient,
    editRecipient,
    removeRecipient
  };
};
